import { onDomReady, querySelectorAll } from 'common/js/utils/dom';
import { loadScriptFromElement } from 'common/js/parts/lazy-js';
// eslint-disable-next-line  @typescript-eslint/no-unused-vars
import appWindowObject from 'common/js/window';

const attributeName = 'data-js-active-on-scroll';
const lazyAttributeName = `${attributeName}-lazy`;
const onceEventAttributeName = `${attributeName}-once-event`;
const isVisibleClassName = 'active-on-scroll_visible';
const eventName = 'ui:on-scroll-visibility';

/**
 * Cached document height to avoid forced reflow on every scroll event.
 * Updated via ResizeObserver and on window resize instead.
 */
let cachedDocHeight = 0;
let docHeightNeedsUpdate = true;

const computeDocHeight = (): number => Math.max(
    document.body.scrollHeight,
    document.documentElement.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.offsetHeight,
    document.body.clientHeight,
    document.documentElement.clientHeight,
);

const getDocHeight = (): number => {
    if (docHeightNeedsUpdate) {
        cachedDocHeight = computeDocHeight();
        docHeightNeedsUpdate = false;
    }

    return cachedDocHeight;
};

const invalidateDocHeight = (): void => {
    docHeightNeedsUpdate = true;
};

enum UnitE {
    pixels = 'px',
    percentage = '%',
}

type ScrolledPositionT = {
    [UnitE.pixels]: number,
    [UnitE.percentage]: number
};

const getScrolledPosition = (): ScrolledPositionT => {
    const winHeight = window.innerHeight || (
        document.documentElement
        || document.body
    ).clientHeight;
    const docHeight = getDocHeight();
    const scrollTop = window.pageYOffset || (
        <HTMLElement>document.documentElement
        || <HTMLElement>document.body.parentNode
        || <HTMLElement>document.body
    ).scrollTop;
    const trackLength = docHeight - winHeight;

    return {
        [UnitE.pixels]: scrollTop,
        [UnitE.percentage]: trackLength > 0 ? Math.floor((scrollTop / trackLength) * 100) : 0,
    };
};

onDomReady(() => {
    const visibleOnScroll = querySelectorAll(`[${attributeName}]`);

    if (visibleOnScroll.length === 0) {
        return;
    }

    // Set up ResizeObserver to invalidate cached doc height on layout changes
    if (typeof ResizeObserver !== 'undefined') {
        const resizeObserver = new ResizeObserver(() => {
            invalidateDocHeight();
        });

        resizeObserver.observe(document.body);
        resizeObserver.observe(document.documentElement);
    }

    // Also invalidate on window resize
    window.addEventListener('resize', invalidateDocHeight, { passive: true });

    // Track which elements have been removed from scroll listening (once-events)
    const removedElements = new WeakSet<HTMLElement>();

    // Use a single rAF-batched scroll handler for all elements
    let scrollRafId: number | null = null;

    const handleScroll = (): void => {
        // Coalesce multiple scroll events into a single rAF callback
        if (scrollRafId !== null) {
            return;
        }

        scrollRafId = requestAnimationFrame(() => {
            scrollRafId = null;

            // Phase 1: Read all geometric properties (no DOM writes here)
            const scrolledPosition = getScrolledPosition();

            // Phase 2: Collect all intended DOM changes
            const writes: Array<{
                element: HTMLElement;
                shouldBeVisible: boolean;
                isOnceEvent: boolean;
                idAttribute: string;
            }> = [];

            visibleOnScroll.forEach((domElement) => {
                if (removedElements.has(domElement)) {
                    return;
                }

                const visibleOnScrollValue = domElement.getAttribute(attributeName) || '';
                const visibleScrollPosition = parseInt(visibleOnScrollValue, 10);
                const unit = (visibleOnScrollValue?.trim().endsWith('px'))
                    ? UnitE.pixels
                    : UnitE.percentage;

                writes.push({
                    element: domElement,
                    shouldBeVisible: scrolledPosition[unit] >= visibleScrollPosition,
                    isOnceEvent: domElement.hasAttribute(onceEventAttributeName),
                    idAttribute: domElement.id,
                });
            });

            // Phase 3: Apply all DOM writes in a single batch (no reads here)
            writes.forEach(({
                element, shouldBeVisible, isOnceEvent, idAttribute,
            }) => {
                if (shouldBeVisible) {
                    loadScriptFromElement(element, lazyAttributeName);
                    element.classList.add(isVisibleClassName);

                    if (idAttribute && window.appWindowObject) {
                        window.appWindowObject.events.emit(eventName, idAttribute, true);
                    }

                    if (isOnceEvent) {
                        removedElements.add(element);
                    }
                } else {
                    element.classList.remove(isVisibleClassName);

                    if (idAttribute) {
                        window.appWindowObject.events.emit(eventName, idAttribute, false);
                    }
                }
            });

            // Invalidate doc height after DOM writes since classList changes
            // may have affected layout
            invalidateDocHeight();
        });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
});

export { };
